import { globalMessageHandler } from "common";
import I18n from "i18n";
import { useState } from "react";
import { BookingItem, setBookingReady, setFoodReady } from "./bookingApi";

export interface UseBookingReadyActionsProps {
  bookingId: number;
  onSuccess?: (updatedBooking: Partial<BookingItem>) => void;
}

export interface UseBookingReadyActionsReturn {
  readyActionLoading:
    | "booking-set"
    | "booking-unset"
    | "food-set"
    | "food-unset"
    | null;
  setBookingReadyStatus: (value: boolean) => Promise<boolean>;
  setFoodReadyStatus: (value: boolean) => Promise<boolean>;
}

export default function useBookingReadyActions({
  bookingId,
  onSuccess,
}: UseBookingReadyActionsProps): UseBookingReadyActionsReturn {
  const [readyActionLoading, setReadyActionLoading] = useState<
    "booking-set" | "booking-unset" | "food-set" | "food-unset" | null
  >(null);

  async function setBookingReadyStatus(value: boolean) {
    setReadyActionLoading(value ? "booking-set" : "booking-unset");
    const { success, data } = await setBookingReady(bookingId, value);
    setReadyActionLoading(null);
    if (success && data) {
      const lastReadyEntry = [...data.booking_ready_history]
        .reverse()
        .find(h => h.ready);
      onSuccess &&
        onSuccess({
          booking_ready_at: data.booking_ready ? lastReadyEntry?.at : null,
          booking_ready_history: data.booking_ready_history,
        });
      globalMessageHandler.snack({
        message: I18n.t(
          value
            ? "Booking.bookingReadySetSuccess"
            : "Booking.bookingReadyUnsetSuccess",
        ),
        severity: "success",
      });
    }
    return !!success;
  }

  async function setFoodReadyStatus(value: boolean) {
    setReadyActionLoading(value ? "food-set" : "food-unset");
    const { success, data } = await setFoodReady(bookingId, value);
    setReadyActionLoading(null);
    if (success && data) {
      const lastReadyEntry = [...data.food_ready_history]
        .reverse()
        .find(h => h.ready);
      onSuccess &&
        onSuccess({
          food_ready_at: data.food_ready ? lastReadyEntry?.at : null,
          food_ready_history: data.food_ready_history,
        });
      globalMessageHandler.snack({
        message: I18n.t(
          value
            ? "Booking.foodReadySetSuccess"
            : "Booking.foodReadyUnsetSuccess",
        ),
        severity: "success",
      });
    }
    return !!success;
  }

  return {
    readyActionLoading,
    setBookingReadyStatus,
    setFoodReadyStatus,
  };
}
